import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/marketplace/Nav";
import Footer from "@/components/marketplace/Footer";
import ProtectedRoute from "@/components/marketplace/ProtectedRoute";
import InstallPrompt from "@/components/marketplace/InstallPrompt";
import CookieConsent from "@/components/marketplace/CookieConsent";
import AnalyticsTracker from "@/components/marketplace/AnalyticsTracker";
import ScrollToTop from "@/components/marketplace/ScrollToTop";

import HomeRoute from "@/components/marketplace/HomeRoute";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import HowItWorks from "@/pages/HowItWorks";
import Outsourcing from "@/pages/Outsourcing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Refund from "@/pages/legal/Refund";
import Auth from "@/pages/Auth";
import AuthVerify from "@/pages/AuthVerify";
import FreelancerApply from "@/pages/FreelancerApply";
import NotFound from "@/pages/NotFound";





// Authenticated areas, split out of the main bundle. Everything above ships in
// one ~1.27MB chunk that every visitor downloads, parses and executes before the
// page becomes interactive — including the admin console, the freelancer portal
// and the CAD studio's 3D viewer, none of which a logged-out visitor can even
// reach. These are deliberately NOT prerendered (nginx serves the empty shell
// for them, see nginx.conf), so a lazy boundary here costs no SEO: the public
// routes above stay statically imported precisely because prerender.js does
// snapshot those, and a Suspense fallback would be what got baked into the HTML.
const SellerDashboard = lazy(() => import("@/pages/seller/SellerDashboard"));
const QuoteDetail = lazy(() => import("@/pages/seller/QuoteDetail"));
const OrderDetail = lazy(() => import("@/pages/seller/OrderDetail"));
const CadStudio = lazy(() => import("@/pages/seller/CadStudio"));

const FreelancerDashboard = lazy(() => import("@/pages/freelancer/FreelancerDashboard"));
const FreelancerJob = lazy(() => import("@/pages/freelancer/FreelancerJob"));
const FreelancerProfile = lazy(() => import("@/pages/freelancer/FreelancerProfile"));

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminClient = lazy(() => import("@/pages/admin/AdminClient"));
const AdminFreelancer = lazy(() => import("@/pages/admin/AdminFreelancer"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminQuote = lazy(() => import("@/pages/admin/AdminQuote"));
const AdminOrder = lazy(() => import("@/pages/admin/AdminOrder"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <ScrollToTop />
        <div className="grain min-h-screen relative">
          {/* First focusable thing in the document, so a keyboard or screen-reader
              visitor can jump the sticky header and its nav on every route instead
              of tabbing through the logo, four nav links and the theme toggle again
              on each page. Hidden until focused (sr-only -> not-sr-only), and a
              plain <a> rather than react-router's <Link> on purpose: the browser's
              own fragment handling is what moves focus, and the pathname never
              changes, so ScrollToTop does not fight it back to the top. */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:inline-flex focus:items-center focus:min-h-11 focus:border focus:border-border focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:tracking-wide focus:text-foreground focus:no-underline"
          >
            Skip to content
          </a>
          <Nav />
          {/* tabIndex -1 because Safari and Firefox move the scroll position to a
              fragment target but not keyboard focus unless the target is focusable;
              without it the skip link scrolls and then hands the next Tab back to
              the header. scroll-mt clears the sticky header, which would otherwise
              sit over the first ~76px of whatever was jumped to. */}
          <main id="main-content" tabIndex={-1} className="relative z-10 scroll-mt-24 focus:outline-none focus:shadow-none">
            <Suspense fallback={<div className="p-20 text-center text-muted-foreground">Loading…</div>}>
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/categories/:slug" element={<CategoryDetail />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/jewellery-cad-outsourcing" element={<Outsourcing />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/refund-policy" element={<Refund />} />
              <Route path="/login" element={<Auth mode="login" />} />
              <Route path="/register" element={<Auth mode="register" />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/freelancer/apply" element={<FreelancerApply />} />

              <Route
                path="/seller"
                element={<ProtectedRoute roles={["seller"]}><SellerDashboard /></ProtectedRoute>}
              />
              <Route
                path="/seller/quotes/:id"
                element={<ProtectedRoute roles={["seller"]}><QuoteDetail /></ProtectedRoute>}
              />
              <Route
                path="/seller/orders/:id"
                element={<ProtectedRoute roles={["seller"]}><OrderDetail /></ProtectedRoute>}
              />
              <Route
                path="/seller/cad-studio"
                element={<ProtectedRoute roles={["seller"]}><CadStudio /></ProtectedRoute>}
              />

              <Route
                path="/freelancer"
                element={<ProtectedRoute roles={["freelancer"]}><FreelancerDashboard /></ProtectedRoute>}
              />
              <Route
                path="/freelancer/jobs/:id"
                element={<ProtectedRoute roles={["freelancer"]}><FreelancerJob /></ProtectedRoute>}
              />
              <Route
                path="/freelancer/profile"
                element={<ProtectedRoute roles={["freelancer"]}><FreelancerProfile /></ProtectedRoute>}
              />

              <Route
                path="/admin"
                element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>}
              />
              <Route
                path="/admin/clients/:id"
                element={<ProtectedRoute roles={["admin"]}><AdminClient /></ProtectedRoute>}
              />
              <Route
                path="/admin/freelancers/:id"
                element={<ProtectedRoute roles={["admin"]}><AdminFreelancer /></ProtectedRoute>}
              />
              <Route
                path="/admin/categories"
                element={<ProtectedRoute roles={["admin"]}><AdminCategories /></ProtectedRoute>}
              />
              <Route
                path="/admin/analytics"
                element={<ProtectedRoute roles={["admin"]}><AdminAnalytics /></ProtectedRoute>}
              />
              <Route
                path="/admin/quotes/:id"
                element={<ProtectedRoute roles={["admin"]}><AdminQuote /></ProtectedRoute>}
              />
              <Route
                path="/admin/orders/:id"
                element={<ProtectedRoute roles={["admin"]}><AdminOrder /></ProtectedRoute>}
              />
              <Route
                path="/admin/settings"
                element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>}
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
          <Toaster position="top-right" />
          <InstallPrompt />
          <CookieConsent />
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
